'use client';

import {
  defaultLoadableState,
  type LoadableState
} from '@carvana/merch-react-hooks';
import deepmerge from 'deepmerge';
import React, { createContext, useContext, useMemo } from 'react';
import useMatchingVehiclesWithDeltas, {
  type MatchingVehicleWithDeltas
} from '~/hooks/new-cars/selectors/useMatchingVehiclesWithDeltas';
import useMatchingVehiclesDeliveryPickup from '~/hooks/new-cars/useMatchingVehiclesDeliveryPickup';
import useMatchingVehiclesPricing from '~/hooks/new-cars/useMatchingVehiclesPricing';
import type { DeepPartial } from '~/types';
import type { TransitChargeInfo } from '~/types/TransitChargeInfo';

type ContextType = {
  fulfillment: LoadableState<Record<number, string>>;
  pricing: LoadableState<Record<number, TransitChargeInfo>>;
  vehicles: MatchingVehicleWithDeltas[];
};

export const defaultContext: ContextType = {
  fulfillment: defaultLoadableState<Record<number, string>>({}),
  pricing: defaultLoadableState<Record<number, TransitChargeInfo>>({}),
  vehicles: []
};

const MatchingVehiclesContext = createContext<ContextType>(defaultContext);

export function MatchingVehiclesProvider({
  children
}: React.PropsWithChildren) {
  const vehicles = useMatchingVehiclesWithDeltas();
  const fulfillment = useMatchingVehiclesDeliveryPickup(vehicles);
  const pricing = useMatchingVehiclesPricing(vehicles);

  const value = useMemo<ContextType>(
    () => ({ fulfillment, pricing, vehicles }),
    [fulfillment, pricing, vehicles]
  );

  return (
    <MatchingVehiclesContext.Provider value={value}>
      {children}
    </MatchingVehiclesContext.Provider>
  );
}

MatchingVehiclesProvider.displayName = 'MatchingVehiclesProvider';

export function useMatchingVehiclesContext() {
  return useContext(MatchingVehiclesContext);
}

export function generateMatchingVehiclesContext(
  customState: DeepPartial<ContextType>
): ContextType {
  return deepmerge<ContextType, DeepPartial<ContextType>>(
    defaultContext,
    customState,
    { arrayMerge: (_target, source) => source }
  );
}

export function MockedMatchingVehiclesProvider({
  children,
  mockValue
}: React.PropsWithChildren<{ mockValue: DeepPartial<ContextType> }>) {
  return (
    <MatchingVehiclesContext.Provider
      value={generateMatchingVehiclesContext(mockValue)}
    >
      {children}
    </MatchingVehiclesContext.Provider>
  );
}

export default MatchingVehiclesContext;
